import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { AtSymbol } from '@styled-icons/heroicons-outline/AtSymbol'
import { PaperClip } from '@styled-icons/heroicons-outline/PaperClip'
import { Trash } from '@styled-icons/heroicons-outline/Trash'
import React, { memo, useCallback, useRef, useState } from 'react'
import { MentionsInput, Mention, DataFunc, OnChangeHandlerFunc, SuggestionDataItem } from 'react-mentions'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import User from 'components/user/User'
import { getUserSearchResult } from 'middleware/actions/customers'
import { USER_SEARCH_PAGINATION } from 'settings/constants/pagination'
import { isImage } from 'utils/file'

import useNoteImage from './useNoteImage'

import './mention-style.scss'

const { INITIAL_PAGE } = USER_SEARCH_PAGINATION
const PLACEHOLDER_SUGGESTION_ID = -1

interface MentionUser {
  email?: string
  firstName?: string
  lastName?: string
  profilePicUri?: string
}

interface MentionSuggestion extends SuggestionDataItem {
  user?: MentionUser
  disabled?: boolean
}

export interface FollowUpNote {
  note?: string
  image?: string | File
  mentions?: MentionUser[]
}

interface AuthState {
  AuthReducer: { userInfo?: { maxFileUploadSize?: string } }
}

interface AdditionalNoteProps {
  note?: FollowUpNote | null
  index?: number
  onChange: (field: 'note' | 'image', value: string | File, index: number) => void
  addAdditionalFollowUps?: (() => void) | null
}

const getFullName = (user?: MentionUser) => `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()

const AdditionalNote = ({ note = null, index = 0, onChange, addAdditionalFollowUps = null }: AdditionalNoteProps) => {
  const additionalNoteRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dispatch = useDispatch()

  const userInfo = useSelector((state: AuthState) => state.AuthReducer.userInfo, shallowEqual)
  const isAuthenticated = !!userInfo

  const [isEmpty, setIsEmpty] = useState(true)
  const [allMentions, setAllMentions] = useState<MentionUser[]>([])

  const maxFileUploadSize = userInfo?.maxFileUploadSize || '2MB'
  const noteImage = note?.image || ''
  const actualNote = note?.note || ''
  const editableSelector = `.div-editable.index-${index}`

  const blobUrl = useNoteImage(noteImage, isAuthenticated)

  const setEditableFocused = useCallback(
    (isFocused: boolean) => document.querySelector(editableSelector)?.classList.toggle('focus', isFocused),
    [editableSelector]
  )

  const resetImage = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    additionalNoteRef.current?.focus()
    setTimeout(() => setEditableFocused(true), 200)
  }, [setEditableFocused])

  const handleFocus = useCallback(() => setEditableFocused(true), [setEditableFocused])

  const handleBlur = useCallback(() => {
    setEditableFocused(false)
    addAdditionalFollowUps?.()
  }, [setEditableFocused, addAdditionalFollowUps])

  const handleNoteChange = useCallback<OnChangeHandlerFunc>((_event, value) => onChange('note', value, index), [onChange, index])

  const handleMentionSearch = useCallback<DataFunc>(
    (search, callback) => {
      if (search === '') {
        setIsEmpty(true)
        return [{ id: PLACEHOLDER_SUGGESTION_ID, display: 'Mention a teammate', disabled: true }]
      }

      dispatch(
        getUserSearchResult({ search, requestedPage: INITIAL_PAGE, requestedPageSize: 2 }, (result: MentionUser[] = []) => {
          const suggestions = result.map(user => ({ id: user.email ?? PLACEHOLDER_SUGGESTION_ID, display: getFullName(user), user }))
          setAllMentions(previous => [...previous, ...result])
          setIsEmpty(suggestions.length === 0)
          callback(suggestions)
        })
      )
      return undefined
    },
    [dispatch]
  )

  const renderSuggestion = useCallback((suggestion: MentionSuggestion, _search: string, highlightedDisplay: React.ReactNode) => {
    if (suggestion.id === PLACEHOLDER_SUGGESTION_ID) {
      return (
        <Stack alignItems="center" flexDirection="row">
          {highlightedDisplay}
        </Stack>
      )
    }

    const userImage = suggestion.user?.profilePicUri ?? ''
    return (
      <Stack alignItems="center" className="user-list" flexDirection="row">
        <User
          enableClick={false}
          firstName={suggestion.user?.firstName ?? ''}
          lastName={suggestion.user?.lastName ?? ''}
          profilePicture={userImage}
          uri={userImage}
        />
        <div className="ml1">{highlightedDisplay}</div>
      </Stack>
    )
  }, [])

  const displayMention = useCallback(
    (id: string) => {
      const mentionedUser = note?.mentions?.find(user => user?.email === id) || allMentions.find(user => user?.email === id)
      return `@${getFullName(mentionedUser)}`
    },
    [note?.mentions, allMentions]
  )

  const handleRemoveImage = useCallback(() => onChange('image', '', index), [onChange, index])

  const handleImageSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      if (!isImage(file.name)) {
        // eslint-disable-next-line no-restricted-syntax -- a file validation message, not an API failure
        toast.error('Error! Only JPG, JPEG & PNG files are allowed')
        resetImage()
        return
      }

      if (file.size / (1024 * 1024) > parseInt(maxFileUploadSize.replace(/[^0-9]/g, ''), 10)) {
        // eslint-disable-next-line no-restricted-syntax -- a file validation message, not an API failure
        toast.error(`Error! Please select image size less than ${maxFileUploadSize}`)
        resetImage()
        return
      }

      onChange('image', file, index)
      resetImage()
    },
    [maxFileUploadSize, onChange, index, resetImage]
  )

  const handleInsertMention = useCallback(() => {
    const separator = actualNote && actualNote.slice(-1) !== ' ' ? ' ' : ''
    onChange('note', `${actualNote}${separator}@`, index)
    additionalNoteRef.current?.focus()
  }, [actualNote, onChange, index])

  return (
    <Box className="call-note" my={1}>
      <div className="full-note text-area-add-container">
        <Stack className={`${noteImage ? 'has-image' : 'no-image'} div-editable index-${index}`} justifyContent="space-between">
          <MentionsInput
            autoComplete="off"
            className={`mentions element-${index} ${isEmpty ? 'empty-list' : 'filled-list'}`}
            inputRef={additionalNoteRef}
            name={`mention-${index}`}
            placeholder={index === 0 ? 'Note' : 'Add a note...'}
            value={actualNote}
            onBlur={handleBlur}
            onChange={handleNoteChange}
            onFocus={handleFocus}
          >
            <Mention
              className="mentions__mention"
              data={handleMentionSearch}
              displayTransform={displayMention}
              markup="[user/__id__]"
              renderSuggestion={renderSuggestion}
              trigger="@"
            />
          </MentionsInput>

          {noteImage && blobUrl && (
            <Stack flexDirection="row" sx={{ mt: 1 }}>
              <img alt="placeholder" className="full-image" src={blobUrl} />
            </Stack>
          )}

          <Box className="bottom-toolbar" display="none">
            {noteImage && (
              <Stack alignItems="center" className="mini-image" flexDirection="row">
                {blobUrl && <img alt="placeholder" className="db preview-image" src={blobUrl} />}
                <Trash className="trash-icon" onClick={handleRemoveImage} />
              </Stack>
            )}

            <Box className="image-upload user-mentions" mt={1}>
              <label htmlFor={`file-input-${index}`}>
                <PaperClip />
                <input ref={fileInputRef} hidden accept="image/*" id={`file-input-${index}`} type="file" onChange={handleImageSelect} />
              </label>

              <Box component="span" ml={1} onClick={handleInsertMention}>
                <AtSymbol />
              </Box>
            </Box>
          </Box>
        </Stack>
      </div>
    </Box>
  )
}

export default memo(AdditionalNote)
